// Peças do suporte partilhadas pelo site e pelo backoffice: estado do pedido, prazo, escolher imagens,
// mostrar anexos e enviar mensagens com anexos (multipart — o cliente tipado da API só fala JSON).
import type { SlaState, Status } from "@ecliptica/contexts-support";
import { ATTACHMENTS_PER_MESSAGE } from "@ecliptica/contexts-support";
import { useTranslation } from "@ecliptica/i18n";
import { Badge, cn } from "@ecliptica/ui";
import { ImagePlus, X } from "lucide-react";
import { useId } from "react";

const STATUS_VARIANT = { open: "warning", in_progress: "default", waiting_player: "secondary", resolved: "success", closed: "outline" } as const;
const SLA_VARIANT = { ok: "success", at_risk: "warning", breached: "destructive", met: "secondary", paused: "outline" } as const;

export function TicketStatusBadge({ status }: { status: Status }) {
  const { t } = useTranslation("common");
  return <Badge variant={STATUS_VARIANT[status]}>{t(`support.status.${status}`)}</Badge>;
}

export function SlaBadge({ state }: { state: SlaState }) {
  const { t } = useTranslation("common");
  return <Badge variant={SLA_VARIANT[state]}>{t(`support.sla.${state}`)}</Badge>;
}

/** Escolher até 3 imagens, com miniaturas e botão para tirar. */
export function ImagePicker({ files, onChange, className }: { files: File[]; onChange: (f: File[]) => void; className?: string }) {
  const { t } = useTranslation("common");
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={id} className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:border-gold hover:text-foreground">
        <ImagePlus className="size-4" /> {t("support.attach")}
      </label>
      <input
        id={id}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="sr-only"
        onChange={(e) => {
          onChange([...files, ...Array.from(e.target.files ?? [])].slice(0, ATTACHMENTS_PER_MESSAGE));
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="relative">
              <img src={URL.createObjectURL(f)} alt={f.name} className="size-16 rounded-md border object-cover" />
              <button
                type="button"
                aria-label="remove"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type AttachmentInfo = { id: string; filename: string; mime: string; size: number };

/** Miniaturas dos anexos de uma mensagem (abrem a imagem em tamanho real). */
export function AttachmentList({ items, token }: { items: AttachmentInfo[]; token?: string | null }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {items.map((a) => {
        const src = `/v1/support/attachments/${a.id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
        return (
          <li key={a.id}>
            <a href={src} target="_blank" rel="noreferrer" title={a.filename}>
              <img src={src} alt={a.filename} loading="lazy" className="size-20 rounded-md border object-cover hover:brightness-110" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/** Envia campos + imagens em multipart. Devolve o estado HTTP e o corpo (JSON). */
export async function postWithFiles(url: string, fields: Record<string, string | undefined>, files: File[]) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v !== undefined) fd.set(k, v);
  for (const f of files) fd.append("files", f);
  const res = await fetch(url, { method: "POST", body: fd, credentials: "include" });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, body };
}

/** Chave de tradução para um erro do suporte devolvido pela API. */
export function supportErrorKey(body: Record<string, unknown>): string {
  const e = typeof body.error === "string" ? body.error : "";
  return ["file_not_image", "file_too_large", "too_many_files", "email_required", "too_many_requests", "closed", "invalid_body", "invalid_transition", "invalid_assignee"].includes(e)
    ? `support.errors.${e}`
    : "error.generic";
}
